import * as Yup from 'yup';
import { useForm, useWatch } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { Grid, Box, DialogTitle, Button, Avatar } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFSwitch, RHFTextField } from '../../components/hook-form';
import { networkCreateGameJoin, networkUpdateGameJoin, useFetchGameJoinCallbackList } from 'src/network/gameJoin';
import { generateSelectMenuItems } from 'src/utils';
import { GeneralSelectMenu } from 'src/components/general';
import useAppInfoStore from 'src/store/appInfo';
import { DialogAnimate } from 'src/components/animate';
import { ContentSelectionTableModal } from 'src/sections/@dashboard/project/feedItems';
import { useQueryClient } from '@tanstack/react-query';
import { SCHEMA1, SCHEMA2 } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { GAME_JOIN_LIST } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

// ----------------------------------------------------------------------

export default function GameJoinForm() {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { gamePlaylistId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { appInfo } = useAppInfoStore();

  // ----------------------------------------------------------------------

  const [isOpen, setIsOpen] = useState(false);

  const [isIncludePayment, setIsIncludePayment] = useState(false);

  const [callbackList, setCallbackList] = useState([]);
  const [selectedCallback, setSelectedCallback] = useState('');

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const [gameDetails, setGameDetails] = useState();

  const gameJoinDetails = modal?.data;
  const isEditMode = !!gameJoinDetails;

  const [filterItems, setFilterItems] = useState({
    ID: -1,
  });

  // ----------------------------------------------------------------------

  const mainSchema = Yup.object().shape({
    ...SCHEMA1.fields,
    ...(isIncludePayment && SCHEMA2.fields),
  });

  const defaultValues = getDefaultValues(gameJoinDetails);

  const methods = useForm({
    // resolver: yupResolver(mainSchema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    control,
  } = methods;

  const includePayment = useWatch({
    control,
    name: 'GameJoinApplicationIsFree',
  });

  // ----------------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      GameJoinApplicationApplicationID: -1,
      GameJoinApplicationGameJoinApplicationPlayListID: +gamePlaylistId,
      GameJoinApplicationTransactionAmountTypeID: selectedAmountType,
      GameJoinApplicationGameID: gameDetails?.GameID,
      GameJoinApplicationIsFree: +values?.GameJoinApplicationIsFree,
      GameJoinApplicationAmount: +values?.GameJoinApplicationAmount,
      GameJoinApplicationGameJoinApplicationCallBackID: selectedCallback,
      GameJoinApplicationHasLeaderBoard: +values?.GameJoinApplicationHasLeaderBoard,
      GameJoinApplicationHasScoreBoard: +values?.GameJoinApplicationHasScoreBoard,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGameJoin(gameJoinDetails?.GameJoinApplicationID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('بازی موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [GAME_JOIN_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGameJoin(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('بازی موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          setGameDetails(null);
          reset();
          await queryClient.invalidateQueries({ queryKey: [GAME_JOIN_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ----------------------------------------------------------

  const { data } = useFetchGameJoinCallbackList(filterItems);

  useEffect(() => {
    if (data) {
      const result = data?.data?.items || [];
      const callbackItems = generateSelectMenuItems(result, 'GameJoinApplicationCallBackLable', 'GameJoinApplicationCallBackID');
      setCallbackList(callbackItems);
      setSelectedCallback(isEditMode ? gameJoinDetails?.GameJoinApplicationCallBackID || callbackItems?.[0]?.id : callbackItems?.[0]?.id);
    }
  }, [data]);

  const onGetAmountTypeList = () => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

    setAmountTypes(amountTypeList);
    setSelectedAmountType(isEditMode ? gameJoinDetails?.TransactionAmountTypeID || amountTypeList[0]?.id : amountTypeList[0]?.id);
  };

  // ----------------------------------------------------------

  useEffect(() => {
    onGetAmountTypeList();
  }, []);

  useEffect(() => {
    if (includePayment) setIsIncludePayment(true);
    else setIsIncludePayment(false);
  }, [includePayment]);

  const selectGameHandler = (item) => {
    setIsOpen(false);
    setGameDetails(item);
    setValue('GameJoinApplicationGameID', item?.GameName);
  };

  useEffect(() => {
    if (isEditMode) {
      setGameDetails(gameJoinDetails);
      setValue('GameJoinApplicationGameID', gameJoinDetails?.GameName);
    }
  }, []);

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField
                  name="GameJoinApplicationGameID"
                  type="text"
                  label="بازی"
                  InputProps={{
                    endAdornment: (
                      <>
                        {gameDetails ? (
                          <Avatar
                            sx={{ cursor: 'pointer' }}
                            alt={gameDetails?.GameName}
                            src={gameDetails?.GameLogo || '/images/fallback.jpeg'}
                            onClick={() => setIsOpen(true)}
                          />
                        ) : (
                          <Button variant="contained" color="inherit" onClick={() => setIsOpen(true)}>
                            انتخاب
                          </Button>
                        )}
                      </>
                    ),
                  }}
                />

                <GeneralSelectMenu
                  label="کال بک"
                  name="GameJoinApplicationGameJoinApplicationCallBackID"
                  options={callbackList}
                  value={selectedCallback}
                  onChangeValue={setSelectedCallback}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
                  mt: 3,
                }}
              >
                <RHFSwitch name="GameJoinApplicationHasLeaderBoard" labelPlacement="end" label="آیا رتبه بندی دارد؟" />

                <RHFSwitch name="GameJoinApplicationHasScoreBoard" labelPlacement="end" label="ایا امتیاز بندی دارد؟" />

                <RHFSwitch name="GameJoinApplicationIsFree" labelPlacement="end" label="آیا شامل پرداخت میشود؟" />

                {includePayment ? (
                  <>
                    <GeneralSelectMenu
                      label="نوع واحد پرداخت"
                      name="GameJoinApplicationTransactionAmountTypeID"
                      options={amountTypes}
                      value={selectedAmountType}
                      onChangeValue={setSelectedAmountType}
                    />
                    <RHFTextField name="GameJoinApplicationAmount" type="text" label="هزینه قابل پرداخت" />
                  </>
                ) : null}
              </Box>

              <div className="flex items-center gap-4 mt-6">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ادامه ویرایش'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>

      <DialogAnimate open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>انتخاب بازی</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          <ContentSelectionTableModal selectContentHandler={selectGameHandler} />
        </div>
      </DialogAnimate>
    </>
  );
}
